import { supabase } from '@/integrations/supabase/client';
import bcrypt from 'bcryptjs';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface RiderSession {
  id: string;
  username: string;
  name: string;
  phone: string;
  email?: string;
  restaurant_id: string;
  role: 'rider';
  vehicle_type?: string;
  current_status: 'offline' | 'available' | 'busy' | 'on_break';
  is_active: boolean;
  average_rating: number;
  total_deliveries: number;
  total_earnings: number;
}

export interface RiderLoginResult {
  success: boolean;
  rider?: RiderSession;
  error?: string;
}

export interface DeliveryDriver {
  id: string;
  restaurant_id: string;
  name: string;
  phone: string;
  email?: string;
  username?: string;
  password_hash?: string;
  role?: string;
  is_login_enabled: boolean;
  last_login_at?: string;
  vehicle_type?: string;
  vehicle_details?: any;
  license_number?: string;
  is_active: boolean;
  current_status: 'offline' | 'available' | 'busy' | 'on_break';
  max_delivery_radius_km: number;
  average_rating: number;
  total_deliveries: number;
  total_earnings: number;
  current_location_lat?: number;
  current_location_lng?: number;
  last_location_update?: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// SESSION STORAGE KEYS
// ============================================================================

const RIDER_SESSION_KEY = 'riderSession';

// ============================================================================
// AUTHENTICATION FUNCTIONS
// ============================================================================

/**
 * Attempt to login a rider with username and password
 * @param username - Rider's username
 * @param password - Rider's password
 * @returns Login result with rider session or error
 */
export async function attemptRiderLogin(
  username: string,
  password: string
): Promise<RiderLoginResult> {
  try {
    if (!username || !password) {
      return {
        success: false,
        error: 'Username and password are required'
      };
    }

    // Fetch rider by username
    const { data: rider, error: fetchError } = await supabase
      .from('delivery_drivers')
      .select('*')
      .eq('username', username.trim().toLowerCase())
      .eq('is_login_enabled', true)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching rider:', fetchError);
      return {
        success: false,
        error: 'Login failed. Please try again.'
      };
    }

    if (!rider) {
      return {
        success: false,
        error: 'Invalid username or password'
      };
    }

    // Check if rider account is active
    if (!rider.is_active) {
      return {
        success: false,
        error: 'Your account has been disabled. Please contact your manager.'
      };
    }

    // Verify password
    if (!rider.password_hash) {
      return {
        success: false,
        error: 'Account not properly configured. Please contact your manager.'
      };
    }

    const passwordMatch = await bcrypt.compare(password, rider.password_hash);

    if (!passwordMatch) {
      return {
        success: false,
        error: 'Invalid username or password'
      };
    }

    // Update last login timestamp
    await supabase
      .from('delivery_drivers')
      .update({
        last_login_at: new Date().toISOString(),
        current_status: 'available' // Auto-set to available on login
      })
      .eq('id', rider.id);

    // Create session object (exclude sensitive data)
    const riderSession: RiderSession = {
      id: rider.id,
      username: rider.username!,
      name: rider.name,
      phone: rider.phone,
      email: rider.email,
      restaurant_id: rider.restaurant_id,
      role: 'rider',
      vehicle_type: rider.vehicle_type,
      current_status: 'available',
      is_active: rider.is_active,
      average_rating: rider.average_rating || 0,
      total_deliveries: rider.total_deliveries || 0,
      total_earnings: rider.total_earnings || 0
    };

    // Store session in localStorage
    localStorage.setItem(RIDER_SESSION_KEY, JSON.stringify(riderSession));

    return {
      success: true,
      rider: riderSession
    };

  } catch (error) {
    console.error('Rider login error:', error);
    return {
      success: false,
      error: 'An unexpected error occurred during login'
    };
  }
}

/**
 * Get the current rider session from localStorage
 * @returns Rider session or null if not logged in
 */
export function getRiderSession(): RiderSession | null {
  try {
    const sessionData = localStorage.getItem(RIDER_SESSION_KEY);
    if (!sessionData) return null;

    const session = JSON.parse(sessionData) as RiderSession;
    
    // Validate session structure
    if (!session.id || !session.username || !session.restaurant_id) {
      logoutRider();
      return null;
    }

    return session;
  } catch (error) {
    console.error('Error reading rider session:', error);
    logoutRider();
    return null;
  }
}

/**
 * Check if a rider is currently logged in
 * @returns true if rider session exists
 */
export function isRiderLoggedIn(): boolean {
  return getRiderSession() !== null;
}

/**
 * Logout the current rider
 * Note: Does NOT change rider status - status persists until manually changed
 */
export function logoutRider(): void {
  // Simply remove session without changing rider status
  // This allows riders to stay "available" even when logged out
  // They must manually set status to "offline" if they want to be unavailable
  localStorage.removeItem(RIDER_SESSION_KEY);
}

/**
 * Update rider session in localStorage
 * @param updates - Partial rider session data to update
 */
export function updateRiderSession(updates: Partial<RiderSession>): void {
  const currentSession = getRiderSession();
  if (!currentSession) return;

  const updatedSession = {
    ...currentSession,
    ...updates
  };

  localStorage.setItem(RIDER_SESSION_KEY, JSON.stringify(updatedSession));
}

// ============================================================================
// RIDER STATUS MANAGEMENT
// ============================================================================

/**
 * Update rider's current status (available, busy, on_break, offline)
 * @param riderId - Rider ID
 * @param status - New status
 */
export async function updateRiderStatus(
  riderId: string,
  status: 'offline' | 'available' | 'busy' | 'on_break'
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('delivery_drivers')
      .update({
        current_status: status,
        updated_at: new Date().toISOString()
      })
      .eq('id', riderId);

    if (error) throw error;

    // Update session
    updateRiderSession({ current_status: status });

    return { success: true };
  } catch (error) {
    console.error('Error updating rider status:', error);
    return {
      success: false,
      error: 'Failed to update status'
    };
  }
}

/**
 * Update rider's location (for GPS tracking features)
 * @param riderId - Rider ID
 * @param lat - Latitude
 * @param lng - Longitude
 */
export async function updateRiderLocation(
  riderId: string,
  lat: number,
  lng: number
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('delivery_drivers')
      .update({
        current_location_lat: lat,
        current_location_lng: lng,
        last_location_update: new Date().toISOString()
      })
      .eq('id', riderId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating rider location:', error);
    return {
      success: false,
      error: 'Failed to update location'
    };
  }
}

// ============================================================================
// RIDER PROFILE FUNCTIONS
// ============================================================================

/**
 * Get full rider profile data
 * @param riderId - Rider ID
 */
export async function getRiderProfile(riderId: string): Promise<DeliveryDriver | null> {
  try {
    const { data, error } = await supabase
      .from('delivery_drivers')
      .select('*')
      .eq('id', riderId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching rider profile:', error);
    return null;
  }
}

/**
 * Change rider password
 * @param riderId - Rider ID
 * @param currentPassword - Current password for verification
 * @param newPassword - New password
 */
export async function changeRiderPassword(
  riderId: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Fetch rider
    const { data: rider, error: fetchError } = await supabase
      .from('delivery_drivers')
      .select('password_hash')
      .eq('id', riderId)
      .single();

    if (fetchError || !rider) {
      return { success: false, error: 'Rider not found' };
    }

    // Verify current password
    const passwordMatch = await bcrypt.compare(currentPassword, rider.password_hash);
    if (!passwordMatch) {
      return { success: false, error: 'Current password is incorrect' };
    }

    // Hash new password
    const newPasswordHash = await bcrypt.hash(newPassword, 10);

    // Update password
    const { error: updateError } = await supabase
      .from('delivery_drivers')
      .update({
        password_hash: newPasswordHash,
        updated_at: new Date().toISOString()
      })
      .eq('id', riderId);

    if (updateError) throw updateError;

    return { success: true };
  } catch (error) {
    console.error('Error changing rider password:', error);
    return {
      success: false,
      error: 'Failed to change password'
    };
  }
}

// ============================================================================
// RIDER CREATION (For Restaurant Owners)
// ============================================================================

export interface CreateRiderData {
  restaurant_id: string;
  name: string;
  phone: string;
  email?: string;
  username: string;
  password: string;
  vehicle_type?: 'car' | 'motorcycle' | 'bicycle' | 'scooter' | 'walking';
  license_number?: string;
  max_delivery_radius_km?: number;
  is_login_enabled?: boolean;
}

/**
 * Create a new rider account (used by restaurant owners)
 * @param riderData - Rider information
 */
export async function createRider(
  riderData: CreateRiderData
): Promise<{ success: boolean; rider?: DeliveryDriver; error?: string }> {
  try {
    // Validate required fields
    if (!riderData.name || !riderData.phone || !riderData.username || !riderData.password) {
      return {
        success: false,
        error: 'Name, phone, username, and password are required'
      };
    }

    // Check if username already exists
    const { data: existing } = await supabase
      .from('delivery_drivers')
      .select('id')
      .eq('username', riderData.username.trim().toLowerCase())
      .maybeSingle();

    if (existing) {
      return {
        success: false,
        error: 'Username already exists'
      };
    }

    // Hash password
    const passwordHash = await bcrypt.hash(riderData.password, 10);

    // Create rider
    const { data: rider, error } = await supabase
      .from('delivery_drivers')
      .insert({
        restaurant_id: riderData.restaurant_id,
        name: riderData.name.trim(),
        phone: riderData.phone.trim(),
        email: riderData.email?.trim(),
        username: riderData.username.trim().toLowerCase(),
        password_hash: passwordHash,
        role: 'rider',
        is_login_enabled: riderData.is_login_enabled !== false,
        vehicle_type: riderData.vehicle_type || 'motorcycle',
        license_number: riderData.license_number,
        max_delivery_radius_km: riderData.max_delivery_radius_km || 10,
        is_active: true,
        current_status: 'offline',
        average_rating: 0,
        total_deliveries: 0,
        total_earnings: 0,
        vehicle_details: {}
      })
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      rider
    };
  } catch (error: any) {
    console.error('Error creating rider:', error);
    
    // Handle specific errors
    if (error.code === '23505') { // Unique violation
      return {
        success: false,
        error: 'Username or phone number already exists'
      };
    }

    return {
      success: false,
      error: 'Failed to create rider account'
    };
  }
}

/**
 * Update rider information (used by restaurant owners)
 * @param riderId - Rider ID
 * @param updates - Fields to update
 */
export async function updateRider(
  riderId: string,
  updates: Partial<DeliveryDriver>
): Promise<{ success: boolean; error?: string }> {
  try {
    // Remove fields that shouldn't be updated directly
    const { id, password_hash, created_at, ...safeUpdates } = updates as any;

    const { error } = await supabase
      .from('delivery_drivers')
      .update({
        ...safeUpdates,
        updated_at: new Date().toISOString()
      })
      .eq('id', riderId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('Error updating rider:', error);
    return {
      success: false,
      error: 'Failed to update rider'
    };
  }
}

/**
 * Delete/deactivate a rider (used by restaurant owners)
 * @param riderId - Rider ID
 * @param hardDelete - If true, permanently delete; if false, just deactivate
 */
export async function deleteRider(
  riderId: string,
  hardDelete: boolean = false
): Promise<{ success: boolean; error?: string }> {
  try {
    if (hardDelete) {
      const { error } = await supabase
        .from('delivery_drivers')
        .delete()
        .eq('id', riderId);

      if (error) throw error;
    } else {
      // Soft delete - just deactivate
      const { error } = await supabase
        .from('delivery_drivers')
        .update({
          is_active: false,
          is_login_enabled: false,
          current_status: 'offline',
          updated_at: new Date().toISOString()
        })
        .eq('id', riderId);

      if (error) throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting rider:', error);
    return {
      success: false,
      error: 'Failed to delete rider'
    };
  }
}
